// ===================================================================
// HMGP — Quarterly Performance Reports
// Quarterly reporting for HMGP projects in implementation phase.
// Reports stored in KV config at hmgp-quarterly-reports.
// ===================================================================

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, Select, Table, Center, Loader,
  Alert, SimpleGrid, ActionIcon, Modal, TextInput, Textarea, Slider,
  Progress,
} from '../../../components/gab/GabComponents';
import {
  IconDownload, IconPlus, IconEdit, IconTrash, IconFileTypePdf,
  IconAlertCircle, IconCalendarStats,
} from '@tabler/icons-react';
import jsPDF from 'jspdf';
import { useHMGPStore } from '../../../store/hmgpStore';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useActivePADisaster as useActiveDisaster } from '../../../hooks/useActiveDisaster';
import { useAuthStore } from '../../../store/authStore';
import { exportToCSV } from '../../../lib/exportUtils';
import { HMGP_PROJECT_TYPE_LABELS } from '../../../types/pagrants';
import type { HMGPProject } from '../../../types/pagrants';
import { WORKER_URL } from '../../../lib/api-switch';

// ── Types ────────────────────────────────────────────────────────

interface HMGPQuarterlyReport {
  id: number;
  projectId: string;
  disasterId: string;
  quarter: string;
  year: string;
  completionPct: string;
  milestonesAchieved: string;
  issuesChallenges: string;
  expendituresThisPeriod: string;
  cumulativeExpenditures: string;
  federalDrawnThisPeriod: string;
  cumulativeFederalDrawn: string;
  projectedCompletionDate: string;
  status: string;
  submittedBy: string;
  submittedDate: string;
  notes: string;
  createdDate: string;
}

const EMPTY_REPORT: Omit<HMGPQuarterlyReport, 'id' | 'disasterId' | 'createdDate'> = {
  projectId: '',
  quarter: 'Q1',
  year: new Date().getFullYear().toString(),
  completionPct: '0',
  milestonesAchieved: '',
  issuesChallenges: '',
  expendituresThisPeriod: '',
  cumulativeExpenditures: '',
  federalDrawnThisPeriod: '',
  cumulativeFederalDrawn: '',
  projectedCompletionDate: '',
  status: 'draft',
  submittedBy: '',
  submittedDate: '',
  notes: '',
};

const KV_KEY = 'hmgp-quarterly-reports';
const API = `${WORKER_URL}/api/config`;
const H: Record<string, string> = { 'Content-Type': 'application/json', Origin: 'https://apps.opengov.com' };
const GREEN = '#059669';

// ── Helpers ──────────────────────────────────────────────────────

function fmtCurrency(v: string | number): string {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(n) || n === 0) return '$0';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function num(v: string | number): number {
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return isNaN(n) ? 0 : n;
}

function currentQuarter(): string {
  const m = new Date().getMonth();
  if (m < 3) return 'Q1';
  if (m < 6) return 'Q2';
  if (m < 9) return 'Q3';
  return 'Q4';
}

function statusColor(s: string): string {
  if (s === 'approved') return 'green';
  if (s === 'submitted') return 'blue';
  return 'gray';
}

// ── KV access ────────────────────────────────────────────────────

async function loadReports(): Promise<HMGPQuarterlyReport[]> {
  try {
    const res = await fetch(`${API}/${KV_KEY}`, { headers: H });
    if (!res.ok) return [];
    const data = await res.json();
    if (typeof data?.value === 'string') return JSON.parse(data.value) as HMGPQuarterlyReport[];
    if (Array.isArray(data)) return data;
    return [];
  } catch { return []; }
}

async function saveReports(reports: HMGPQuarterlyReport[]): Promise<void> {
  await fetch(`${API}/${KV_KEY}`, {
    method: 'PUT',
    headers: H,
    body: JSON.stringify({ value: JSON.stringify(reports) }),
  });
}

// ── PDF generation ───────────────────────────────────────────────

function generateReportPDF(report: HMGPQuarterlyReport, project: HMGPProject | undefined, orgName: string) {
  const doc = new jsPDF();
  const w = doc.internal.pageSize.getWidth();
  let y = 20;

  const addLine = (label: string, value: string, bold = false) => {
    if (bold) doc.setFont('helvetica', 'bold');
    else doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(label, 14, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(value ?? ''), 90, y);
    y += 7;
    if (y > 270) { doc.addPage(); y = 20; }
  };

  const addSection = (title: string) => {
    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(title, 14, y);
    y += 8;
  };

  const addWrapped = (label: string, value: string) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(label, 14, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(String(value || 'N/A'), w - 28);
    doc.text(lines, 14, y);
    y += lines.length * 5 + 4;
    if (y > 270) { doc.addPage(); y = 20; }
  };

  // Header
  doc.setFillColor(5, 150, 105);
  doc.rect(0, 0, w, 14, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('HMGP Quarterly Performance Report', w / 2, 10, { align: 'center' });
  doc.setTextColor(0, 0, 0);
  y = 24;

  addSection('Project Information');
  addLine('Project Number:', project?.projectNumber || 'N/A');
  addLine('Project Title:', project?.projectTitle || 'N/A');
  addLine('Project Type:', project ? (HMGP_PROJECT_TYPE_LABELS[project.projectType as keyof typeof HMGP_PROJECT_TYPE_LABELS] || project.projectType) : 'N/A');
  addLine('Organization:', orgName || 'N/A');

  addSection('Report Period');
  addLine('Quarter:', `${report.quarter} ${report.year}`);
  addLine('Status:', report.status.charAt(0).toUpperCase() + report.status.slice(1));
  addLine('Submitted By:', report.submittedBy || 'N/A');
  addLine('Submitted Date:', report.submittedDate || 'N/A');

  addSection('Progress');
  addLine('Completion %:', `${report.completionPct}%`);
  addLine('Projected Completion:', report.projectedCompletionDate || 'N/A');

  addSection('Financial');
  addLine('Expenditures This Period:', fmtCurrency(report.expendituresThisPeriod));
  addLine('Cumulative Expenditures:', fmtCurrency(report.cumulativeExpenditures));
  addLine('Federal Drawn This Period:', fmtCurrency(report.federalDrawnThisPeriod));
  addLine('Cumulative Federal Drawn:', fmtCurrency(report.cumulativeFederalDrawn));

  addWrapped('Milestones Achieved:', report.milestonesAchieved);
  addWrapped('Issues / Challenges:', report.issuesChallenges);
  addWrapped('Notes:', report.notes);

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text(`Generated ${new Date().toLocaleDateString()} | OpenGov EMC`, w / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });

  doc.save(`HMGP_Quarterly_${report.quarter}_${report.year}_${project?.projectNumber || 'unknown'}.pdf`);
}

// ── Component ────────────────────────────────────────────────────

export default function HMGPQuarterlyReports() {
  const { projects, fetchProjects } = useHMGPStore();
  const { organizations, fetchOrganizations } = usePAGrantsStore();
  const activeDisaster = useActiveDisaster();
  const userEmail = useAuthStore(s => s.user?.email ?? '');

  const [reports, setReports] = useState<HMGPQuarterlyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingReport, setEditingReport] = useState<HMGPQuarterlyReport | null>(null);
  const [form, setForm] = useState(EMPTY_REPORT);
  const [saving, setSaving] = useState(false);

  // ── Bootstrap ──────────────────────────────────────────────────

  useEffect(() => {
    Promise.all([fetchProjects(), fetchOrganizations(), loadReports().then(setReports)])
      .finally(() => setLoading(false));
  }, []);

  // ── Derived data ───────────────────────────────────────────────

  const disasterReports = useMemo(() => {
    if (!activeDisaster) return [];
    return reports.filter(r => String(r.disasterId) === String(activeDisaster.Rid));
  }, [reports, activeDisaster]);

  const disasterProjects = useMemo(() => {
    if (!activeDisaster) return [];
    return projects.filter(p => String(p.disasterId) === String(activeDisaster.Rid));
  }, [projects, activeDisaster]);

  const implProjects = useMemo(() =>
    disasterProjects.filter(p => p.phase === 'implementation'),
  [disasterProjects]);

  const orgMap = useMemo(() => {
    const m: Record<string, string> = {};
    organizations.forEach(o => { m[String(o.Rid)] = o.orgName; });
    return m;
  }, [organizations]);

  const projectMap = useMemo(() => {
    const m: Record<string, HMGPProject> = {};
    disasterProjects.forEach(p => { m[String(p.Rid)] = p; });
    return m;
  }, [disasterProjects]);

  const curQ = currentQuarter();
  const curY = new Date().getFullYear().toString();

  const missingCount = useMemo(() => {
    const reported = new Set(
      disasterReports.filter(r => r.quarter === curQ && r.year === curY).map(r => r.projectId),
    );
    return implProjects.filter(p => !reported.has(String(p.Rid))).length;
  }, [disasterReports, implProjects, curQ, curY]);

  const submittedCount = useMemo(() =>
    disasterReports.filter(r => r.status === 'submitted' || r.status === 'approved').length,
  [disasterReports]);

  // ── CRUD ───────────────────────────────────────────────────────

  const openCreate = useCallback(() => {
    setEditingReport(null);
    setForm({ ...EMPTY_REPORT, year: curY, quarter: curQ });
    setModalOpen(true);
  }, [curQ, curY]);

  const openEdit = useCallback((r: HMGPQuarterlyReport) => {
    setEditingReport(r);
    setForm({
      projectId: r.projectId,
      quarter: r.quarter,
      year: r.year,
      completionPct: r.completionPct,
      milestonesAchieved: r.milestonesAchieved,
      issuesChallenges: r.issuesChallenges,
      expendituresThisPeriod: r.expendituresThisPeriod,
      cumulativeExpenditures: r.cumulativeExpenditures,
      federalDrawnThisPeriod: r.federalDrawnThisPeriod,
      cumulativeFederalDrawn: r.cumulativeFederalDrawn,
      projectedCompletionDate: r.projectedCompletionDate,
      status: r.status,
      submittedBy: r.submittedBy,
      submittedDate: r.submittedDate,
      notes: r.notes,
    });
    setModalOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!activeDisaster || !form.projectId) return;
    setSaving(true);
    try {
      let updated: HMGPQuarterlyReport[];
      const now = new Date().toISOString().slice(0, 10);
      if (editingReport) {
        updated = reports.map(r =>
          r.id === editingReport.id
            ? {
                ...r,
                ...form,
                submittedBy: form.status === 'submitted' && !r.submittedBy ? userEmail : form.submittedBy,
                submittedDate: form.status === 'submitted' && !r.submittedDate ? now : form.submittedDate,
              }
            : r,
        );
      } else {
        const maxId = reports.reduce((mx, r) => Math.max(mx, r.id), 0);
        const newReport: HMGPQuarterlyReport = {
          id: maxId + 1,
          disasterId: String(activeDisaster.Rid),
          ...form,
          submittedBy: form.status === 'submitted' ? userEmail : '',
          submittedDate: form.status === 'submitted' ? now : '',
          createdDate: now,
        };
        updated = [...reports, newReport];
      }
      await saveReports(updated);
      setReports(updated);
      setModalOpen(false);
    } finally {
      setSaving(false);
    }
  }, [activeDisaster, editingReport, form, reports, userEmail]);

  const handleDelete = useCallback(async (id: number) => {
    const updated = reports.filter(r => r.id !== id);
    await saveReports(updated);
    setReports(updated);
  }, [reports]);

  // ── CSV Export ─────────────────────────────────────────────────

  const handleExport = useCallback(() => {
    const headers = [
      'Period', 'Project #', 'Project Title', 'Organization', 'Completion %',
      'Expenditures This Period', 'Cumulative Expenditures',
      'Federal Drawn This Period', 'Cumulative Federal Drawn',
      'Projected Completion', 'Status', 'Submitted By', 'Submitted Date', 'Notes',
    ];
    const rows = disasterReports.map(r => {
      const p = projectMap[r.projectId];
      return [
        `${r.quarter} ${r.year}`,
        p?.projectNumber || '',
        p?.projectTitle || '',
        p ? (orgMap[p.organizationId] || '') : '',
        r.completionPct,
        r.expendituresThisPeriod,
        r.cumulativeExpenditures,
        r.federalDrawnThisPeriod,
        r.cumulativeFederalDrawn,
        r.projectedCompletionDate,
        r.status,
        r.submittedBy,
        r.submittedDate,
        r.notes,
      ];
    });
    exportToCSV('hmgp_quarterly_reports', headers, rows);
  }, [disasterReports, projectMap, orgMap]);

  // ── Render ─────────────────────────────────────────────────────

  if (loading) return <Center h={200}><Loader color={GREEN} /></Center>;

  if (!activeDisaster) {
    return (
      <Stack p="md">
        <Title order={3}>HMGP Quarterly Reports</Title>
        <Alert icon={<IconAlertCircle size={16} />} color="yellow" title="No Active Disaster">
          Select an active incident in the top bar to view quarterly reports.
        </Alert>
      </Stack>
    );
  }

  const projectOptions = implProjects.map(p => ({
    value: String(p.Rid),
    label: `${p.projectNumber} — ${p.projectTitle}`,
  }));

  return (
    <Stack p="md">
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={3}>HMGP Quarterly Reports</Title>
          <Text size="sm" c="dimmed">Performance reporting for HMGP projects in implementation</Text>
        </div>
        <Group gap="xs">
          <Button leftSection={<IconDownload size={16} />} variant="light" color="teal" onClick={handleExport} disabled={disasterReports.length === 0}>
            Export CSV
          </Button>
          <Button leftSection={<IconPlus size={16} />} color={GREEN} onClick={openCreate}>
            New Report
          </Button>
        </Group>
      </Group>

      {/* ── Stats ─────────────────────────────────────────── */}
      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: `3px solid ${GREEN}` }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Total Reports</Text>
          <Text size="xl" fw={700}>{disasterReports.length}</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #2563EB' }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Submitted / Approved</Text>
          <Text size="xl" fw={700}>{submittedCount}</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: `3px solid ${missingCount > 0 ? '#D97706' : GREEN}` }}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Missing ({curQ} {curY})</Text>
          <Text size="xl" fw={700} c={missingCount > 0 ? 'orange' : GREEN}>{missingCount}</Text>
        </Paper>
      </SimpleGrid>

      {/* ── Table ─────────────────────────────────────────── */}
      {disasterReports.length === 0 ? (
        <Paper p="xl" withBorder ta="center">
          <IconCalendarStats size={40} color="#9CA3AF" style={{ margin: '0 auto 8px' }} />
          <Text c="dimmed">No quarterly reports yet. Click "New Report" to create one.</Text>
        </Paper>
      ) : (
        <Paper withBorder style={{ overflow: 'auto' }}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Period</Table.Th>
                <Table.Th>Project</Table.Th>
                <Table.Th>Completion</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Expenditures</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Cumulative</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th style={{ textAlign: 'center' }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {disasterReports.map(r => {
                const p = projectMap[r.projectId];
                const pct = num(r.completionPct);
                return (
                  <Table.Tr key={r.id}>
                    <Table.Td fw={600}>{r.quarter} {r.year}</Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>{p?.projectNumber || '—'}</Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>{p?.projectTitle || '—'}</Text>
                    </Table.Td>
                    <Table.Td style={{ minWidth: 120 }}>
                      <Group gap={6} wrap="nowrap">
                        <Progress value={pct} color={GREEN} size="sm" style={{ flex: 1 }} />
                        <Text size="xs" fw={600}>{pct}%</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{fmtCurrency(r.expendituresThisPeriod)}</Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>{fmtCurrency(r.cumulativeExpenditures)}</Table.Td>
                    <Table.Td>
                      <Badge color={statusColor(r.status)} variant="light" size="sm">
                        {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="center" wrap="nowrap">
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="blue"
                          title="Export PDF"
                          onClick={() => generateReportPDF(r, p, p ? orgMap[p.organizationId] || '' : '')}
                        >
                          <IconFileTypePdf size={16} />
                        </ActionIcon>
                        {r.status === 'draft' && (
                          <>
                            <ActionIcon size="sm" variant="subtle" color="blue" title="Edit" onClick={() => openEdit(r)}>
                              <IconEdit size={16} />
                            </ActionIcon>
                            <ActionIcon size="sm" variant="subtle" color="red" title="Delete" onClick={() => handleDelete(r.id)}>
                              <IconTrash size={16} />
                            </ActionIcon>
                          </>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* ── Create / Edit Modal ───────────────────────────── */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingReport ? 'Edit Quarterly Report' : 'New Quarterly Report'}
        size="lg"
      >
        <Stack gap="sm">
          <Select
            label="Project"
            placeholder="Select an implementation project"
            data={projectOptions}
            value={form.projectId}
            onChange={v => setForm(f => ({ ...f, projectId: v || '' }))}
            searchable
            required
          />

          <Group grow>
            <Select
              label="Quarter"
              data={['Q1', 'Q2', 'Q3', 'Q4']}
              value={form.quarter}
              onChange={v => setForm(f => ({ ...f, quarter: v || 'Q1' }))}
              required
            />
            <Select
              label="Year"
              data={Array.from({ length: 5 }, (_, i) => String(new Date().getFullYear() - 2 + i))}
              value={form.year}
              onChange={v => setForm(f => ({ ...f, year: v || curY }))}
              required
            />
          </Group>

          <div>
            <Text size="sm" fw={500} mb={4}>Completion Percentage: {form.completionPct}%</Text>
            <Slider
              value={num(form.completionPct)}
              onChange={v => setForm(f => ({ ...f, completionPct: String(v) }))}
              min={0}
              max={100}
              step={1}
              color="teal"
              marks={[{ value: 0, label: '0%' }, { value: 25 }, { value: 50, label: '50%' }, { value: 75 }, { value: 100, label: '100%' }]}
              mb="sm"
            />
          </div>

          <Textarea
            label="Milestones Achieved"
            placeholder="Describe milestones completed this quarter..."
            value={form.milestonesAchieved}
            onChange={e => setForm(f => ({ ...f, milestonesAchieved: e.target.value }))}
            minRows={3}
          />

          <Textarea
            label="Issues / Challenges"
            placeholder="Describe any issues or challenges encountered..."
            value={form.issuesChallenges}
            onChange={e => setForm(f => ({ ...f, issuesChallenges: e.target.value }))}
            minRows={3}
          />

          <SimpleGrid cols={2}>
            <TextInput
              label="Expenditures This Period ($)"
              placeholder="0"
              value={form.expendituresThisPeriod}
              onChange={e => setForm(f => ({ ...f, expendituresThisPeriod: e.target.value }))}
            />
            <TextInput
              label="Cumulative Expenditures ($)"
              placeholder="0"
              value={form.cumulativeExpenditures}
              onChange={e => setForm(f => ({ ...f, cumulativeExpenditures: e.target.value }))}
            />
            <TextInput
              label="Federal Funds Drawn This Period ($)"
              placeholder="0"
              value={form.federalDrawnThisPeriod}
              onChange={e => setForm(f => ({ ...f, federalDrawnThisPeriod: e.target.value }))}
            />
            <TextInput
              label="Cumulative Federal Funds Drawn ($)"
              placeholder="0"
              value={form.cumulativeFederalDrawn}
              onChange={e => setForm(f => ({ ...f, cumulativeFederalDrawn: e.target.value }))}
            />
          </SimpleGrid>

          <TextInput
            label="Projected Completion Date"
            placeholder="YYYY-MM-DD"
            value={form.projectedCompletionDate}
            onChange={e => setForm(f => ({ ...f, projectedCompletionDate: e.target.value }))}
          />

          <Textarea
            label="Notes"
            placeholder="Additional notes..."
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            minRows={2}
          />

          <Select
            label="Status"
            data={[
              { value: 'draft', label: 'Draft' },
              { value: 'submitted', label: 'Submitted' },
              { value: 'approved', label: 'Approved' },
            ]}
            value={form.status}
            onChange={v => setForm(f => ({ ...f, status: v || 'draft' }))}
          />

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button color={GREEN} onClick={handleSave} loading={saving} disabled={!form.projectId}>
              {editingReport ? 'Save Changes' : 'Create Report'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
